import { useMemo } from 'react';
import { Card, Col, Row, Spin, Typography } from 'antd';
import { IconStethoscope, IconBuildingFactory2, IconArrowUpRight } from '@tabler/icons-react';
import { Link, Navigate } from 'react-router-dom';
import { useAuthSession } from '@/entities/auth/api/hooks';
import { resolvePostLoginPath } from '@/entities/auth/lib/resolvePostLoginPath';
import { useDashboardWidgets } from '@/entities/dashboard/api/hooks';
import { useMedicalSummaryWidget, useMedicalRiskWidget } from '@/entities/medical/api/medicalChecksApi';
import { useTrainingSummaryWidget } from '@/entities/training/api/libraryApi';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { DashboardWidget } from '@/entities/dashboard/model/dashboardSchema';
import type { SummaryWidget, RiskWidget } from '@/entities/medical/model/medicalChecksSchema';
import type { TrainingSummaryWidget } from '@/entities/training/model/librarySchema';
import type { Status } from '@/entities/user/model/userSchema';
import s from './Dashboard.module.scss';
import { ReadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';

const { Title } = Typography;

const DOMAIN_ICON: Record<string, React.ReactNode> = {
  medical: <IconStethoscope size={16} />,
  training: <ReadOutlined size={16} />,
  sout: <IconBuildingFactory2 size={16} />,
  siz: <SafetyCertificateOutlined size={16} />,
};

const STATUS_LABEL: Record<Status, string> = {
  err: 'Критично',
  warn: 'Внимание',
  ok: 'В норме',
  info: 'Информация',
};

const RISK_SEVERITY_TO_STATUS: Record<string, Status> = {
  critical: 'err',
  high: 'err',
  medium: 'warn',
  low: 'ok',
};

function WidgetCard({ widget }: { widget: DashboardWidget }) {
  const problemsTone = widget.status;

  return (
    <Card className={s.root__widget} classNames={{ body: s['root__widget-body'] }}>
      <div className={s['root__widget-header']}>
        <div className={s['root__widget-heading']}>
          <div className={s['root__widget-title-row']}>
            <span className={s['root__widget-icon']}>{DOMAIN_ICON[widget.domain]}</span>
            <Title level={5} className={s['root__widget-title']}>
              {widget.title}
            </Title>
          </div>
        </div>
        <StatusBadge status={widget.status} label={STATUS_LABEL[widget.status]} />
      </div>

      <div className={s['root__metrics-grid']}>
        {widget.metrics.map((m) => {
          return (
            <div key={m.label} className={s.root__metric}>
              <span className={s['root__metric-value']}>{m.value}</span>
              <span className={s['root__metric-label']}>{m.label}</span>
            </div>
          );
        })}
      </div>

      {widget.metricsHint ? <div className={s.root__metricsHint}>{widget.metricsHint}</div> : null}

      {widget.problems.length > 0 ? (
        <div className={`${s['root__problems-section']} ${s[`root__problems-section_${problemsTone}`]}`}>
          <div className={`${s['root__problems-title']} ${s[`root__problems-title_${problemsTone}`]}`}>
            <span>Требуют внимания</span>
          </div>
          <ul className={s['root__problems-list']}>
            {widget.problems.map((p) => {
              const muted =
                p.startsWith('И ещё') ||
                p.startsWith('Просроченных') ||
                p.startsWith('Просроченные') ||
                p.startsWith('Критичных') ||
                p.startsWith('Критических');
              return (
                <li
                  key={p}
                  className={`${s['root__problem-item']} ${s[`root__problem-item_${problemsTone}`]} ${
                    muted ? s['root__problem-item_muted'] : ''
                  }`}
                >
                  {p}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <Link to={widget.linkTo} className={s['root__widget-link']}>
        Перейти к разделу
        <IconArrowUpRight size={16} />
      </Link>
    </Card>
  );
}

/** Панель «требуют внимания»: строки как в обучении (ФИО — тип осмотра, что сделать). */
function buildMedicalAttentionProblems(summary: SummaryWidget, risk: RiskWidget | null | undefined): string[] {
  const lines = summary.attention_items.map((item) => `${item.employee_name} — ${item.exam_label}, ${item.detail}`);
  if (risk && risk.total_at_risk > summary.attention_items.length) {
    const more = risk.total_at_risk - summary.attention_items.length;
    if (more > 0) {
      lines.push(`И ещё ${more} сотр. — см. раздел «Медосмотры».`);
    }
  }
  if (
    lines.length === 0 &&
    summary.overdue_count === 0 &&
    summary.not_allowed_count === 0 &&
    (!risk || risk.total_at_risk === 0)
  ) {
    lines.push('Просроченных и критичных случаев нет');
  }
  return lines;
}

function buildMedicalWidget(summary: SummaryWidget, risk: RiskWidget | null | undefined): DashboardWidget {
  const statusFromRisk = risk ? RISK_SEVERITY_TO_STATUS[risk.severity] : undefined;
  const status: Status =
    statusFromRisk ?? (summary.overdue_count > 0 ? 'err' : summary.not_allowed_count > 0 ? 'warn' : 'ok');

  const problems = buildMedicalAttentionProblems(summary, risk);

  return {
    domain: 'medical',
    title: 'Медосмотры',
    linkTo: '/medical?tab=employees',
    status,
    metricsHint: 'Метрики — количество всех сотрудников по статусам медосмотров',
    metrics: [
      { label: 'Всего в контингенте', value: summary.contingent_total },
      { label: 'Запланировано', value: summary.planned_count },
      { label: 'Пройдено', value: summary.passed_count },
      {
        label: 'Просрочено',
        value: summary.overdue_count,
      },
      {
        label: 'Не допущены',
        value: summary.not_allowed_count,
      },
    ],
    problems,
  };
}

function buildTrainingWidget(w: TrainingSummaryWidget): DashboardWidget {
  const status: Status = w.overdue > 0 ? 'err' : w.expires_in_30_days > 0 ? 'warn' : 'ok';

  const problems: string[] = w.attention_items.map(
    (item) => `${item.employee_name} — ${item.program_name}, ${item.detail}`,
  );
  if (w.attention_items.length === 0 && w.overdue === 0 && w.expires_in_30_days === 0) {
    problems.push('Просроченных и горящих случаев нет');
  }

  return {
    domain: 'training',
    title: 'Обучение и инструктажи',
    linkTo: '/training?tab=records',
    status,
    metricsHint: 'Метрики — количество из назначений (обучений/инструктажей) по сотрудникам',
    metrics: [
      {
        label: 'Просрочено',
        value: w.overdue,
        severity: w.overdue > 0 ? 'err' : undefined,
      },
      {
        label: 'Истекает 30 дн.',
        value: w.expires_in_30_days,
        severity: w.expires_in_30_days > 0 ? 'warn' : undefined,
      },
      { label: 'В работе', value: w.in_progress },
    ],
    problems,
  };
}

export function Dashboard() {
  const { data: auth, isLoading: authLoading } = useAuthSession();
  const { data: mockWidgets, isLoading: wL } = useDashboardWidgets();
  const { data: medSummary, isLoading: medL } = useMedicalSummaryWidget();

  const canFetchMedicalRisk = !authLoading && auth?.user.role !== 'employee';
  const { data: medRisk, isLoading: medRiskL } = useMedicalRiskWidget({ enabled: canFetchMedicalRisk });

  const { data: trainSummary, isLoading: trainL } = useTrainingSummaryWidget();

  const widgets = useMemo(() => {
    const result: DashboardWidget[] = [];
    if (medSummary) {
      result.push(buildMedicalWidget(medSummary, canFetchMedicalRisk ? medRisk : undefined));
    }
    if (trainSummary) result.push(buildTrainingWidget(trainSummary));
    const otherWidgets = (mockWidgets ?? []).filter((w) => w.domain !== 'medical' && w.domain !== 'training');
    result.push(...otherWidgets);
    return result;
  }, [medSummary, trainSummary, mockWidgets, medRisk, canFetchMedicalRisk]);

  if (!authLoading && auth?.user.role === 'employee' && auth.user.employee_id) {
    return <Navigate to={resolvePostLoginPath(auth.user, '/')} replace />;
  }

  if (wL && !mockWidgets) return <PageSpinner />;

  const widgetsSpinning = medL || trainL || authLoading || (canFetchMedicalRisk && medRiskL);

  return (
    <Spin spinning={widgetsSpinning}>
      <Row gutter={[20, 20]}>
        {widgets.map((w) => (
          <Col xs={24} md={12} key={w.domain}>
            <WidgetCard widget={w} />
          </Col>
        ))}
      </Row>
    </Spin>
  );
}
